import { type WorkflowStepType } from '@/api/workflows';

/**
 * The `appInfo.bmoStatus` ladder — which statuses the advisor runtime authors, when it
 * authors them, and which ones belong to BMO alone.
 *
 * Mirrors `apps/back-end/src/BMO.WorkflowPlatform.Api/Bmo/BmoStatus.cs` on the wire values and
 * `Bmo/BmoStatusLadder.cs` on the policy. The wire strings are BMO's, not ours; the RANKING
 * below is ours, and it is the whole of the transition rule:
 *
 *   passing (Next'ing off) a step  =>  status = max(current, milestoneForStep(type))
 *   editing on a step              =>  status = min(current, ceilingForStep(type))
 *
 * Everything outside the five rungs (every `ESIGN-*`, `APP-DELETED`, `CLIENT-ESIGNCOMPLETE`,
 * `ALL-ESIGN-COMPLETE`, `CLOSED`, and anything BMO invents after this ships) is BMO's: we pass
 * it through untouched, never author it, and render the application read-only.
 *
 * ## Absent steps never award their rung
 *
 * A workflow with no AdditionalDocuments step can never reach DOC-UPLOAD-COMPLETE, and that is
 * correct — claiming documents were uploaded when the workflow never asked for any would be a
 * lie to BMO. The consequence lands on whoever writes the future submit gate: it must check
 * "every milestone step PRESENT in this workflow has been passed", not a fixed rank of
 * DOC-UPLOAD-COMPLETE, or it will deadlock every document-free workflow. There is deliberately
 * no `highestReachableStatus(steps)` helper here yet — nothing consumes it today.
 *
 * That gate now exists — `BmoStatusLadder.IsReadyToSubmit` — and it reads PRESENT exactly as
 * written above: authored on the template. **Which leaves a known gap this paragraph did not
 * foresee: a step present but hidden by the advisor's own ANSWERS.** It still raises the rank the
 * gate demands, and the advisor can never earn that rung, so submit deadlocks. Found 2026-10-02,
 * not yet fixed — the full account is on `IsReadyToSubmit`'s doc comment and in PROJECT_PLAN.md.
 * Relevant here because the two files are mirrors: a condition-aware fix on that side wants
 * `evaluateConditions` applied to the same step list this side already filters for the rail.
 */
export const BMO_STATUS = {
  /**
   * The bottom rung, held for exactly one round trip: a new application is seeded here, the
   * create call carries it, and {@link promoteFromInitiated} moves it to IN-PROGRESS the moment
   * BMO answers. It is therefore the one rung that is never re-sent, and an application resumed
   * from an id never visits it at all.
   *
   * Note the underscore — BMO's spelling, deliberately not normalised to match its hyphenated
   * neighbours.
   */
  applicationInitiated: 'APPLICATION_INITIATED',
  inProgress: 'IN-PROGRESS',
  reviewComplete: 'REVIEW-COMPLETE',
  esignerReviewComplete: 'ESIGNER-REVIEW-COMPLETE',
  docUploadComplete: 'DOC-UPLOAD-COMPLETE',
  /** Authored by the future dedicated submit call, never by a save. Terminal. */
  submitted: 'SUBMITTED',
} as const;

/** One of the six statuses the platform reasons about. */
export type LadderStatus = (typeof BMO_STATUS)[keyof typeof BMO_STATUS];

const RANK: Record<LadderStatus, number> = {
  [BMO_STATUS.applicationInitiated]: 0,
  [BMO_STATUS.inProgress]: 1,
  [BMO_STATUS.reviewComplete]: 2,
  [BMO_STATUS.esignerReviewComplete]: 3,
  [BMO_STATUS.docUploadComplete]: 4,
  [BMO_STATUS.submitted]: 5,
};

/**
 * Milestone EARNED by passing (Next'ing off) a step. `null` means passing it earns nothing.
 *
 * Welcome / DynamicForm are `null` rather than IN-PROGRESS because advancing is a `max` — the
 * bottom rung would be a no-op anyway, and `null` reads as the truth ("passing this earns
 * nothing") while letting the caller skip the write entirely.
 *
 * PdfGeneration is `null` DELIBERATELY: SUBMITTED belongs to the dedicated submit function, not
 * to the save API. Reaching the last step is not submitting it.
 */
export function milestoneForStep(type: WorkflowStepType): LadderStatus | null {
  switch (type) {
    case 'ApplicationReview':
      return BMO_STATUS.reviewComplete;
    case 'OneSpanESign':
      return BMO_STATUS.esignerReviewComplete;
    case 'AdditionalDocuments':
      return BMO_STATUS.docUploadComplete;
    // ThankYou earns nothing for the same reason PdfGeneration does, and it is worth
    // saying out loud because it is the step that LOOKS like it should award SUBMITTED.
    // SUBMITTED belongs to the Submit button's own dedicated call, which commits it
    // directly; landing on the confirmation card is the CONSEQUENCE of that call having
    // already succeeded. Awarding it here as well would let a save re-assert a rung the
    // submit endpoint owns.
    case 'Welcome':
    case 'DynamicForm':
    case 'PdfGeneration':
    case 'PdfFileUpload':
    case 'ThankYou':
      return null;
  }
}

/**
 * Ceiling IMPOSED by editing on a step — the milestone earned by the step before it, so an edit
 * costs the advisor exactly the rungs their change invalidated. `null` means the step has no
 * edit surface at all.
 *
 * ApplicationReview and PdfGeneration are `null` because neither is wired with a change handler
 * (FormShell's StepContent passes them no `onChange` / `mutateValues` / uploader) — they are
 * recap surfaces. A demotion arriving from one would mean a wiring bug, and demoting on a bug is
 * worse than not demoting. If a future ticket gives ApplicationReview inline editing, its
 * correct ceiling is IN-PROGRESS and this is the one line to change.
 */
export function ceilingForStep(type: WorkflowStepType): LadderStatus | null {
  switch (type) {
    case 'Welcome':
    case 'DynamicForm':
      return BMO_STATUS.inProgress;
    case 'OneSpanESign':
      return BMO_STATUS.reviewComplete;
    case 'AdditionalDocuments':
      return BMO_STATUS.esignerReviewComplete;
    // ThankYou joins them with no edit surface at all — no change handler, and the shell
    // renders no way back off it. Nothing can arrive from it to demote.
    case 'ApplicationReview':
    case 'PdfGeneration':
    case 'PdfFileUpload':
    case 'ThankYou':
      return null;
  }
}

/** Position on the ladder, or `null` when the value is not one of ours. */
export function rankOf(status: string | null | undefined): number | null {
  if (!status) return null;
  const rank = RANK[status as LadderStatus];
  return rank === undefined ? null : rank;
}

/**
 * On the ladder AND below SUBMITTED — i.e. the advisor runtime may still move this application.
 *
 * Note this is NOT the exact negation of {@link isLocked}: both answer `false` for a missing
 * status, because "we don't know yet" is neither writable nor a reason to lock.
 */
export function isAdvisorWritable(status: string | null | undefined): boolean {
  const rank = rankOf(status);
  return rank !== null && rank < RANK[BMO_STATUS.submitted];
}

/**
 * BMO owns this application now: it is SUBMITTED, or a status off our ladder entirely. The
 * runtime renders it read-only.
 *
 * A missing status answers `false` on purpose. Resume answers 501 until BMO's retrieve contract
 * lands (blockers B5 / B15), so "no status yet" is the COMMON path for a fresh application —
 * treating it as locked would bar the advisor from every form they start.
 */
export function isLocked(status: string | null | undefined): boolean {
  if (!status) return false;
  return !isAdvisorWritable(status);
}

/**
 * `max(current, milestone)` — passing a step can only ever move the status up.
 *
 * Returns `current` untouched when it is not ours to move (a BMO-owned status, SUBMITTED, or a
 * value we do not recognise) or when the step earns nothing.
 */
export function advanceStatus(current: string, milestone: LadderStatus | null): string {
  if (milestone === null || !isAdvisorWritable(current)) return current;
  return RANK[milestone] > (rankOf(current) ?? 0) ? milestone : current;
}

/**
 * `min(current, ceiling)` — editing on a step can only ever move the status down.
 *
 * Same guard as {@link advanceStatus}: a status we do not own passes straight through, so a
 * stray edit can never overwrite what BMO put there.
 */
export function demoteStatus(current: string, ceiling: LadderStatus | null): string {
  if (ceiling === null || !isAdvisorWritable(current)) return current;
  return RANK[ceiling] < (rankOf(current) ?? 0) ? ceiling : current;
}

/**
 * Where a freshly mounted application starts, before BMO has confirmed anything.
 *
 * The distinction is whether the application EXISTS, not whether we know its status. A `/new`
 * application has not been created yet, so the save that follows is the create call and starts on
 * the bottom rung. One reached by id already exists — resume answers 501 until B5 / B15 land, so
 * "no confirmed status" is the common path there too, and seeding it at APPLICATION_INITIATED
 * would tell BMO an application they already hold is being created.
 */
export function initialStatusFor(applicationId: string | null | undefined): LadderStatus {
  return applicationId ? BMO_STATUS.inProgress : BMO_STATUS.applicationInitiated;
}

/**
 * The one-way step off the bottom rung, applied as soon as a save lands.
 *
 * APPLICATION_INITIATED describes the create call and nothing after it, so it is spent the moment
 * BMO answers — the application is now in progress by definition. Written as a transition rather
 * than folded into {@link advanceStatus} because it is earned by the SAVE succeeding, not by
 * passing a step: the first save usually leaves a Welcome or DynamicForm step, and those earn no
 * milestone at all.
 *
 * Anything above the bottom rung passes straight through, so this is safe to apply after every
 * save rather than only the first.
 */
export function promoteFromInitiated(current: string): string {
  return current === BMO_STATUS.applicationInitiated ? BMO_STATUS.inProgress : current;
}

/** Which locked-banner copy a status deserves. */
export type LockedReason = 'submitted' | 'esign' | 'closed' | 'deleted' | 'generic';

/**
 * Every status in BMO's vocabulary that means "this application is out with OneSpan". Listed
 * explicitly rather than pattern-matched on the `ESIGN-` prefix: `ALL-ESIGN-COMPLETE` and
 * `CLIENT-ESIGNCOMPLETE` do not carry it, and BMO's spelling is inconsistent enough
 * (`ESIGNER-REVIEW-COMPLETE` vs `ESIGN-IN-PROGRESS`) that a prefix rule would be a trap.
 */
const ESIGN_STATUSES = new Set([
  'ESIGN-PACKAGE-RECEIVED',
  'ESIGN-PACKAGE-CREATED',
  'ESIGN-INVITATION-SENT',
  'ESIGN-IN-PROGRESS',
  'ESIGN-DECLINED',
  'ESIGN-EXPIRED',
  'ESIGN-STOPPED-INPROGRESS',
  'CLIENT-ESIGNCOMPLETE',
  'ALL-ESIGN-COMPLETE',
]);

/**
 * Maps a locked status onto one of five reason buckets, so the banner needs five strings rather
 * than one per status in BMO's vocabulary. The `generic` fallback interpolates the raw wire code,
 * so a status BMO adds after this ships still says something true.
 */
export function lockedReasonKey(status: string): LockedReason {
  if (status === BMO_STATUS.submitted) return 'submitted';
  if (status === 'APP-DELETED') return 'deleted';
  if (status === 'CLOSED') return 'closed';
  if (ESIGN_STATUSES.has(status)) return 'esign';
  return 'generic';
}
